import AppExtensionsSDK, { Event } from '@pipedrive/app-extensions-sdk';
import { useCallback, useEffect, useRef, useState } from 'react';
import ErrorReporter from 'src/monitoring/errorReporter';
import type {
  PipedriveIntegration,
  PipedriveOutgoingCallContext,
  PipedriveVisibilityEvent,
} from './types';
import { usePhoneContext } from 'src/contexts/phone-context';
import { hideFloatingWindow, showFloatingWindow } from './utils';
import type { CallProvider, CallProviderData } from 'hooks/phones/types';
import STATES from 'src/states/index';

const DEVELOPMENT_ENV = import.meta.env.DEV;

export const useInitPipedriveIntegration = (
  sdk: AppExtensionsSDK | null,
  callProvider: CallProvider
): PipedriveIntegration => {
  useSetPipedriveEventHandlers(sdk, callProvider);

  const { setPhoneVisibility } = useSetPipedrivePhoneVisibility(sdk);

  return {
    setPhoneVisibility,
  };
};

export const useInitPipedriveSdk = (
  identifier: string
): { sdk: AppExtensionsSDK | null } => {
  const [sdk, setSdk] = useState<AppExtensionsSDK | null>(null);

  const initializeSDK = useCallback(async () => {
    try {
      const sdk = await new AppExtensionsSDK({
        identifier,
      }).initialize();

      setSdk(sdk);
    } catch (error) {
      console.log('Failed to initialize AppExtensionsSDK:', error);
    }
  }, [identifier]);

  useEffect(() => {
    if (!DEVELOPMENT_ENV) {
      void initializeSDK();
    }
  }, [initializeSDK]);

  return { sdk };
};

const useSetPipedriveEventHandlers = (
  sdk: AppExtensionsSDK | null,
  callProvider: CallProvider
) => {
  const activeAutoCallKeyRef = useRef<string | null>(null);
  const pendingAutoCallKeyRef = useRef<string | null>(null);

  const handleVisibilityEvent = useCallback(
    async ({ error, data }: PipedriveVisibilityEvent) => {
      if (error) {
        ErrorReporter.sendMessage(error, 'error');
        return;
      }

      console.log({ data });

      if (data?.is_visible !== true) {
        activeAutoCallKeyRef.current = null;
        pendingAutoCallKeyRef.current = null;
        return;
      }

      const context = data.context;
      if (!isPipedriveOutgoingCallContext(context)) return;

      const autoCallKey = buildAutoCallKey(context);
      const callAlreadyStartedForVisibleContext =
        activeAutoCallKeyRef.current === autoCallKey ||
        pendingAutoCallKeyRef.current === autoCallKey;
      if (callAlreadyStartedForVisibleContext) return;

      pendingAutoCallKeyRef.current = autoCallKey;
      try {
        await callProvider.callActions.startCall({
          phoneNumber: context.callToNumber,
          callProviderData: buildProviderData(context),
        });
        activeAutoCallKeyRef.current = autoCallKey;
      } finally {
        if (pendingAutoCallKeyRef.current === autoCallKey) {
          pendingAutoCallKeyRef.current = null;
        }
      }
    },
    [callProvider.callActions]
  );

  const subscribeVisibilityEvents = useCallback(() => {
    if (!sdk) {
      const unsubscribeFn = () => undefined;
      return unsubscribeFn;
    }

    const unsubscribeFn = sdk.listen<Event.VISIBILITY>(
      Event.VISIBILITY,
      (event: PipedriveVisibilityEvent) => {
        void handleVisibilityEvent(event).catch((error: unknown) => {
          reportPipedriveSdkError(
            'Failed to handle Pipedrive visibility event',
            error
          );
        });
      }
    );

    return unsubscribeFn;
  }, [handleVisibilityEvent, sdk]);

  useEffect(() => {
    const unsubscribeVisibilityEvents = subscribeVisibilityEvents();

    const unsubscribeAll = () => {
      unsubscribeVisibilityEvents();
    };
    return () => {
      unsubscribeAll();
    };
  }, [subscribeVisibilityEvents]);
};

const isPipedriveOutgoingCallContext = (
  context: NonNullable<PipedriveVisibilityEvent['data']>['context']
): context is PipedriveOutgoingCallContext => {
  if (!isRecord(context)) return false;
  if (typeof context.callToNumber !== 'string') return false;
  if (context.callToNumber.trim() === '') return false;
  if (context.invoker !== 'command' && context.invoker !== 'user') return false;

  return hasValidRelatedIds(context.relatedIds);
};

const hasValidRelatedIds = (relatedIds: unknown): boolean => {
  if (relatedIds === undefined) return true;
  if (!isRecord(relatedIds)) return false;

  return (
    isOptionalNumber(relatedIds.dealId) &&
    isOptionalNumber(relatedIds.orgId) &&
    isOptionalNumber(relatedIds.personId)
  );
};

const isOptionalNumber = (value: unknown): boolean =>
  value === undefined || typeof value === 'number';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const buildProviderData = (
  context: PipedriveOutgoingCallContext
): CallProviderData | undefined => {
  if (!context.relatedIds) return undefined;

  return {
    providerName: 'pipedrive',
    contactProviderId: context.relatedIds.personId?.toString(),
    opportunityProviderId: context.relatedIds.dealId?.toString(),
  };
};

const buildAutoCallKey = (context: PipedriveOutgoingCallContext): string =>
  JSON.stringify({
    callToNumber: context.callToNumber,
    dealId: context.relatedIds?.dealId ?? null,
    orgId: context.relatedIds?.orgId ?? null,
    personId: context.relatedIds?.personId ?? null,
  });

const reportPipedriveSdkError = (message: string, error: unknown): void => {
  ErrorReporter.sendMessage(message, 'error');
  ErrorReporter.sendException(error);
};

const useSetPipedrivePhoneVisibility = (sdk: AppExtensionsSDK | null) => {
  const { setKeyboardVisible, phoneState } = usePhoneContext();

  const setPhoneVisibility = useCallback(
    (visibility: boolean) => {
      if (!sdk) return;

      setKeyboardVisible(visibility);
      const visibilityAction = visibility
        ? showFloatingWindow(sdk)
        : hideFloatingWindow(sdk);

      void visibilityAction.catch((error: unknown) => {
        reportPipedriveSdkError(
          'Failed to update Pipedrive floating window visibility',
          error
        );
      });
    },
    [sdk, setKeyboardVisible]
  );

  useEffect(() => {
    if (phoneState !== STATES.PHONE.INCOMING) return;

    setPhoneVisibility(true);
  }, [phoneState, setPhoneVisibility]);

  return { setPhoneVisibility };
};
